import { Bell } from 'lucide-react';
import { Popover } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useNotificationsFeatureState, useNotificationsUnreadCounts } from '@/lib/endpoints';
import {
  useCloseNotificationsPanel,
  useNotificationsPanelOpen,
  useNotificationsRingNonce,
  useToggleNotificationsPanel,
} from '@/store/notificationsStore';
import { formatBadgeCount } from '@/lib/notifications/formatBadgeCount';
import { NotificationsPanel } from '../Notifications/NotificationsPanel';

/**
 * "Notifications" row pinned to the bottom of the desktop {@link SideNav}.
 *
 * Same data/store wiring as {@link NotificationBell} (which stays the mobile
 * entry point in the top bar), but rendered as a sidebar cell with the unread
 * badge inline next to the label, per the Figma side menu. The panel opens in
 * a {@link Popover} anchored to the cell — upward and to the content side, so
 * it clears the bottom viewport edge the cell sits against. Hidden entirely
 * when the notifications kill-switch reports the feature off.
 */
export function SideNavNotifications() {
  const { data: feature } = useNotificationsFeatureState();
  const { data: counts } = useNotificationsUnreadCounts();
  const count = counts?.all ?? 0;
  const panelOpen = useNotificationsPanelOpen();
  const togglePanel = useToggleNotificationsPanel();
  const closePanel = useCloseNotificationsPanel();
  const ringNonce = useNotificationsRingNonce();

  // Hide when the feature is explicitly killed (show while the gate is still
  // loading to avoid a flash-then-disappear) — mirrors NotificationBell.
  if (feature?.enabled === false) return null;

  const cell = (
    <button
      type="button"
      data-testid="sidenav-notifications"
      aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
      onClick={togglePanel}
      className="flex h-11 w-full items-center gap-2.5 rounded-lg px-4 text-ink transition-colors hover:bg-primary/[0.03]"
    >
      {/* key remount replays the ring on every arrival (nonce bump). */}
      <Bell key={ringNonce} aria-hidden="true" className={cn('size-5 shrink-0', ringNonce > 0 && 'animate-bell-ring')} />
      <span className="text-base tracking-[0.32px]">Notifications</span>
      {count > 0 && (
        <span
          data-testid="sidenav-notifications-badge"
          className="flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-alt px-1 text-[11px] font-semibold leading-none text-white"
        >
          {formatBadgeCount(count)}
        </span>
      )}
    </button>
  );

  return (
    <Popover
      open={panelOpen}
      onClose={closePanel}
      anchor={cell}
      align="start"
      className="bottom-full top-auto mb-2 mt-0 w-[400px] max-w-[calc(100vw-2rem)]"
    >
      <NotificationsPanel />
    </Popover>
  );
}
